import type {
    HttpMethod,
    BodyChunk
} from '../../types/web/http';
import type {
    ClientHttpRequest,
    ClientHttpResponse
} from '../../types/web/client';

import type { ClientHttpConnector } from './types.ts';
import { AbstractClientHttpRequest, AbstractClientHttpResponse } from './exchange.ts';
import { MapHttpHeaders } from '../http/headers.ts';
import { httpStatusCode } from '../http/status.ts';

class FetchClientHttpRequest extends AbstractClientHttpRequest implements ClientHttpRequest {
    #body?: BodyInit;
    constructor(method: HttpMethod, uri: URL) {
        super(method, uri);
    }

    async body(body: ReadableStream<BodyChunk> | Promise<BodyChunk> | BodyChunk): Promise<boolean> {
        this.#body = body instanceof ReadableStream ? body : await body;
        return await this.end();
    }

    end() {
        return Promise.resolve(true);
    }

    getNativeRequest<T>(): T {
        const method = this.method;
        const headers = new Headers();
        for (const key of this.headers.keys()) {
            if (key.toLowerCase() === 'content-length') {
                continue; // Content-Length is set automatically later
            }
            for (const value of this.headers.list(key)) {
                headers.append(key, value);
            }
        }
        if (!this.headers.has('accept')) {
            headers.append('accept', '*/*');
        }
        const body = this.#body;

        return new Request(this.URL, {method, headers, body}) as T;
    }
}
function toHttpHeaders(headers: Headers): MapHttpHeaders {
    const result = new MapHttpHeaders();
    for (const [key, value] of headers.entries()) {
        result.add(key, value);
    }
    return result;
}
class FetchClientHttpResponse extends AbstractClientHttpResponse implements ClientHttpResponse {
    #response: Response;
    constructor(response: Response) {
        super(httpStatusCode(response.status), toHttpHeaders(response.headers));
        this.#response = response;
    }

    get statusMessage() {
        return this.#response.statusText;
    }

    get body() {
        return this.#response.body ?? undefined;
    }

    blob() {
        return this.#response.blob();
    }
}

export class FetchClientHttpConnector implements ClientHttpConnector {
    readonly #fetch: typeof fetch;
    constructor(fetchFn: typeof fetch = globalThis.fetch) {
        this.#fetch = fetchFn;
    }

    async connect(method: HttpMethod, uri: URL, requestCallback: (request: ClientHttpRequest) => Promise<void>): Promise<ClientHttpResponse> {
        const fetchClientHttpRequest = new FetchClientHttpRequest(method, uri);
        await requestCallback(fetchClientHttpRequest);
        const fetchRequest = fetchClientHttpRequest.getNativeRequest<Request>();
        const fetchResponse = await this.#fetch(fetchRequest);
        return new FetchClientHttpResponse(fetchResponse);
    }
}
